import React, { memo, useCallback } from "react";
import { Button, CircularProgress } from "@mui/material";
import { Col } from "react-bootstrap";

type ButtonVariant = "text" | "outlined" | "contained";

export type Service = {
  id_action: number;
  label: string;
  type: "url" | "function";
  value: string;
  variant?: ButtonVariant;
};

function defaultIconByService(value: number) {
  switch (value) {
    case 1:
      return "change.svg";
    case 2:
      return "credit-card.svg"; // fixed typo
    case 3:
      return "invoice.svg";
    case 4:
      return "email.svg";
    case 5:
      return "electricity.svg";
    case 6:
      return "helmet.svg";
    case 7:
      return "gas-tank2.svg";
    case 8:
      return "Wallet.svg";
    case 9:
      return "arrange-square.svg";
    case 10:
      return "invoice.svg";
    case 11:
      return "update.svg";
    case 12:
      return "green-invoice.svg";
    case 13:
      return "danger.svg";
    case 14:
      return "water-heat.svg";
    case 15:
      return "gas-tank.svg";
    case 16:
      return "mobility.svg";
    default:
      return "";
  }
}

export default memo(function ServicesCategorySection({
  isActive,
  onAvatarClick, // kept for compatibility
  services,
  loading,
  onServiceSelect,
  iconBasePath = "svg",
  iconByService = defaultIconByService,
}: {
  isActive: boolean;
  onAvatarClick: () => void;
  services: Service[];
  loading: boolean;
  onServiceSelect?: (srv: Service) => void;
  /** Optional: override icon folder path (defaults to "svg") */
  iconBasePath?: string;
  /** Optional: provide custom icon resolver */
  iconByService?: (id_action: number) => string;
}) {
  const handleClick = useCallback(
    (srv: Service) => {
      // Push GTM event
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: "service_button_click",
        service_label: srv.label,
        service_type: srv.type,
      });

      // Delegate all behavior (including URL opens) to parent to avoid double-actions
      onServiceSelect?.(srv);
    },
    [onServiceSelect],
  );

  if (!isActive) return null;

  if (loading) {
    return <CircularProgress sx={{ mt: 2 }} />;
  }

  return (
    <div className="row">
      {services.map((srv) => {
        const { id_action, label, value, variant } = srv;
        const iconFile = iconByService(id_action);
        const showIcon = !!iconFile;

        return (
          <Col key={`${label}-${value}-${id_action}`} sm={4}>
            <Button
              fullWidth
              variant={variant ?? "outlined"}
              onClick={() => handleClick(srv)}
              data-action-id={id_action}
              aria-label={label}
              sx={{
                mb: 2,
                borderColor: "primary.main",
                borderRadius: 3,
                color: "primary.main",
                fontWeight: 800,
                fontSize: "1.3rem",
                textTransform: "none",
                pl: 0,
                minHeight: 56,
                position: "relative",
                overflow: "hidden",
                justifyContent: "center",
              }}
            >
              <span style={{ color: "black" }}>{label}</span>
              {showIcon && (
                <img
                  src={`${iconBasePath}/${iconFile}`}
                  alt=""
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 4,
                    position: "absolute",
                    right: 25,
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                  }}
                />
              )}
            </Button>
          </Col>
        );
      })}
    </div>
  );
});
